import { useState, useCallback, useRef, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDataProvider, useNotify, Title } from 'react-admin';
import { ScopePicker } from './ScopePicker';
import { HP_DOMAINS } from '../constants/hpDomains';
import { apiFetch } from '../providers/api-rest-provider';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'orchestra_onboarding_step';

const STEPS = [
  'Organization Setup',
  'Build Org Tree',
  'Invite Admins',
  'Import Roster',
  'Create First Assessment',
  'Allocate Seats',
];

const TAXONOMY_PACKS = [
  { value: 'us_air_force', label: 'US Air Force' },
  { value: 'us_army', label: 'US Army' },
  { value: 'us_navy', label: 'US Navy' },
  { value: 'us_marine_corps', label: 'US Marine Corps' },
  { value: 'socom', label: 'SOCOM' },
];

const ROLES = [
  { value: 'tenant_admin', label: 'Tenant Admin' },
  { value: 'echelon_commander', label: 'Echelon Commander' },
  { value: 'instructor', label: 'Instructor' },
  { value: 'readonly', label: 'Read Only' },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InviteRow {
  email: string;
  role: string;
}

interface CsvRow {
  firstName: string;
  lastName: string;
  email: string;
  orgPath: string;
  _errors: string[];
}

// ---------------------------------------------------------------------------
// Step 1 — Organization Setup
// ---------------------------------------------------------------------------

interface Step1State {
  orgName: string;
  taxonomyPack: string;
}

function Step1OrgSetup({
  state,
  onChange,
}: {
  state: Step1State;
  onChange: (s: Step1State) => void;
}) {
  return (
    <Stack spacing={3} mt={2}>
      <TextField
        label="Organization Name"
        value={state.orgName}
        onChange={(e) => onChange({ ...state, orgName: e.target.value })}
        fullWidth
        required
      />
      <TextField
        label="Service Taxonomy Pack"
        select
        value={state.taxonomyPack}
        onChange={(e) => onChange({ ...state, taxonomyPack: e.target.value })}
        fullWidth
        helperText="Selecting a pack seeds role and echelon templates for your service branch."
      >
        {TAXONOMY_PACKS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Build Org Tree
// ---------------------------------------------------------------------------

interface OrgUnit {
  id: string;
  name: string;
  path: string;
}

function Step2OrgTree() {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [units, setUnits] = useState<OrgUnit[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newParent, setNewParent] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (loaded) return;
    setLoading(true);
    try {
      const { data } = await dataProvider.getList('org_units', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'path', order: 'ASC' },
        filter: {},
      });
      setUnits(data as OrgUnit[]);
      setLoaded(true);
    } catch {
      notify('Failed to load org units', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, loaded, notify]);

  // Load on first render
  useState(() => { load(); });

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const slug = newName.trim().toLowerCase().replace(/\s+/g, '-');
      const path = newParent ? `${newParent}/${slug}` : `/${slug}`;
      const { data } = await dataProvider.create('org_units', {
        data: { name: newName.trim(), path },
      });
      setUnits((prev) => [...prev, data as OrgUnit].sort((a, b) => a.path.localeCompare(b.path)));
      setNewName('');
      notify('Org unit created', { type: 'success' });
    } catch {
      notify('Failed to create org unit', { type: 'error' });
    } finally {
      setAdding(false);
    }
  };

  return (
    <Box mt={2}>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Build your organizational hierarchy. Each unit maps to an echelon in the selected taxonomy.
      </Typography>

      {loading && <CircularProgress size={24} />}

      {units.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 2, maxHeight: 240, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Path</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {units.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">
                      {u.path}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" mb={1}>
        Add Level
      </Typography>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <TextField
          label="Unit Name"
          size="small"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          sx={{ flex: 1 }}
        />
        <ScopePicker
          value={newParent}
          onChange={setNewParent}
          label="Parent (optional)"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={adding || !newName.trim()}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {adding ? 'Adding...' : 'Add Unit'}
        </Button>
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Invite Admins
// ---------------------------------------------------------------------------

function Step3InviteAdmins({
  rows,
  onChange,
}: {
  rows: InviteRow[];
  onChange: (r: InviteRow[]) => void;
}) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [sending, setSending] = useState(false);

  const addRow = () => onChange([...rows, { email: '', role: 'tenant_admin' }]);

  const removeRow = (i: number) => onChange(rows.filter((_, idx) => idx !== i));

  const update = (i: number, field: keyof InviteRow, value: string) => {
    const next = [...rows];
    next[i] = { ...next[i], [field]: value };
    onChange(next);
  };

  const handleSend = async () => {
    const valid = rows.filter((r) => r.email.trim());
    if (!valid.length) {
      notify('Add at least one email address', { type: 'warning' });
      return;
    }
    setSending(true);
    let sent = 0;
    for (const row of valid) {
      try {
        // Create the user record, then attach the role assignment
        const { data: newUser } = await dataProvider.create('users', {
          data: { email: row.email, type: 'invite', status: 'pending' },
        });
        await dataProvider.create('role_assignments', {
          data: { userId: newUser.id, role: row.role, orgPath: '/' },
        });
        sent++;
      } catch {
        // Continue with remaining rows
      }
    }
    setSending(false);
    if (sent > 0) {
      notify(`Invited ${sent} of ${valid.length} user(s)`, {
        type: sent === valid.length ? 'success' : 'warning',
      });
    } else {
      notify('Failed to send invites — check console for details', { type: 'error' });
    }
  };

  return (
    <Box mt={2}>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Invite team members to the platform. They will receive an email with login instructions.
      </Typography>

      <Stack spacing={2}>
        {rows.map((row, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <TextField
              label="Email"
              type="email"
              size="small"
              value={row.email}
              onChange={(e) => update(i, 'email', e.target.value)}
              sx={{ flex: 2 }}
            />
            <TextField
              label="Role"
              select
              size="small"
              value={row.role}
              onChange={(e) => update(i, 'role', e.target.value)}
              sx={{ flex: 1 }}
            >
              {ROLES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            <IconButton size="small" onClick={() => removeRow(i)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Box>
          <Button startIcon={<AddIcon />} size="small" onClick={addRow}>
            Add Row
          </Button>
        </Box>
        <Box>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Send Invites'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Import Roster
// ---------------------------------------------------------------------------

const REQUIRED_COLUMNS = ['firstName', 'lastName', 'email', 'orgPath'];

function parseCsv(raw: string): CsvRow[] {
  const lines = raw.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const values = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = values[i] ?? ''; });
    const errors: string[] = [];
    REQUIRED_COLUMNS.forEach((col) => {
      if (!row[col]) errors.push(`Missing ${col}`);
    });
    if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
      errors.push('Invalid email');
    }
    return {
      firstName: row.firstName ?? '',
      lastName: row.lastName ?? '',
      email: row.email ?? '',
      orgPath: row.orgPath ?? '',
      _errors: errors,
    };
  });
}

function Step4ImportRoster() {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [rows, setRows] = useState<CsvRow[]>([]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setRows(parseCsv(text));
    };
    reader.readAsText(file);
  };

  const validRows = rows.filter((r) => r._errors.length === 0);
  const errorRows = rows.filter((r) => r._errors.length > 0);

  const handleImport = async () => {
    if (!validRows.length) return;
    setImporting(true);
    let success = 0;
    for (const row of validRows) {
      try {
        await dataProvider.create('students', {
          data: {
            first_name: row.firstName,
            last_name: row.lastName,
            email: row.email,
            org_path: row.orgPath,
          },
        });
        success++;
      } catch {
        // continue importing others
      }
    }
    setImporting(false);
    notify(`Imported ${success} of ${validRows.length} students`, {
      type: success === validRows.length ? 'success' : 'warning',
    });
  };

  return (
    <Box mt={2}>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Upload a CSV with columns: <code>firstName, lastName, email, orgPath</code>
      </Typography>

      <input
        type="file"
        accept=".csv"
        ref={fileRef}
        style={{ display: 'none' }}
        onChange={handleFile}
      />
      <Button variant="outlined" onClick={() => fileRef.current?.click()} sx={{ mb: 2 }}>
        Choose CSV File
      </Button>

      {rows.length > 0 && (
        <>
          <Alert severity={errorRows.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
            {rows.length} rows parsed — {validRows.length} valid, {errorRows.length} with errors
          </Alert>

          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Org Path</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow
                    key={i}
                    sx={{ bgcolor: row._errors.length ? 'error.light' : undefined }}
                  >
                    <TableCell>{row.firstName}</TableCell>
                    <TableCell>{row.lastName}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.orgPath}</TableCell>
                    <TableCell>
                      {row._errors.length ? (
                        <Typography variant="caption" color="error">
                          {row._errors.join('; ')}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="success.main">
                          OK
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing || validRows.length === 0}
          >
            {importing ? 'Importing...' : `Import ${validRows.length} Students`}
          </Button>
        </>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — Create First Assessment
// ---------------------------------------------------------------------------

interface Step5State {
  name: string;
  type: string;
}

function Step5Assessment({
  state,
  onChange,
}: {
  state: Step5State;
  onChange: (s: Step5State) => void;
}) {
  return (
    <Stack spacing={3} mt={2}>
      <TextField
        label="Assessment Name"
        value={state.name}
        onChange={(e) => onChange({ ...state, name: e.target.value })}
        fullWidth
        required
      />
      <TextField
        select
        label="Assessment Type"
        value={state.type}
        onChange={(e) => onChange({ ...state, type: e.target.value })}
        fullWidth
        helperText="Human-performance domain (per DoD Total Force Fitness)"
      >
        {HP_DOMAINS.map((d) => (
          <MenuItem key={d.id} value={d.id}>
            {d.name}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 6 — Allocate Seats
// ---------------------------------------------------------------------------

interface Step6State {
  orgPath: string;
  seatCount: number;
}

function Step6AllocateSeats({
  state,
  onChange,
}: {
  state: Step6State;
  onChange: (s: Step6State) => void;
}) {
  return (
    <Stack spacing={3} mt={2}>
      <Typography variant="body2" color="text.secondary">
        Select an org unit to allocate licensed seats to.
      </Typography>
      <ScopePicker
        value={state.orgPath}
        onChange={(path) => onChange({ ...state, orgPath: path })}
        label="Org Unit"
      />
      <TextField
        label="Seat Count"
        type="number"
        value={state.seatCount}
        onChange={(e) => onChange({ ...state, seatCount: Number(e.target.value) })}
        inputProps={{ min: 1 }}
        sx={{ maxWidth: 200 }}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main Wizard
// ---------------------------------------------------------------------------

// Checklist keys in step order — used to derive the first incomplete step from server state
const CHECKLIST_STEP_KEYS = [
  'org_setup',
  'org_tree',
  'admins_invited',
  'roster_imported',
  'assessment_created',
  'seats_allocated',
] as const;

export const OnboardingWizard = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const savedStep = Number(localStorage.getItem(STORAGE_KEY) ?? '0');
  const [activeStep, setActiveStep] = useState<number>(
    Math.min(savedStep, STEPS.length - 1),
  );

  // Per-step state
  const [step1, setStep1] = useState<Step1State>({ orgName: '', taxonomyPack: '' });
  const [step3Rows, setStep3Rows] = useState<InviteRow[]>([{ email: '', role: 'tenant_admin' }]);
  const [step5, setStep5] = useState<Step5State>({ name: '', type: '' });
  const [step6, setStep6] = useState<Step6State>({ orgPath: '', seatCount: 0 });

  const [submitting, setSubmitting] = useState(false);

  const persist = (step: number) => {
    localStorage.setItem(STORAGE_KEY, String(step));
  };

  // Load server state on mount — server wins over localStorage
  useEffect(() => {
    apiFetch('tenants/self').then((self: any) => {
      const checklist: Record<string, boolean> = self?.checklist ?? {};
      const firstIncomplete = CHECKLIST_STEP_KEYS.findIndex((k) => !checklist[k]);
      const serverStep = firstIncomplete === -1 ? STEPS.length - 1 : firstIncomplete;
      setActiveStep(serverStep);
      persist(serverStep);
      if (self?.name) {
        setStep1((prev) => ({ ...prev, orgName: self.name }));
      }
      if (self?.settings?.taxonomyPack) {
        setStep1((prev) => ({ ...prev, taxonomyPack: self.settings.taxonomyPack }));
      }
    }).catch(() => {
      // Network failure — keep localStorage step
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = async () => {
    setSubmitting(true);
    try {
      if (activeStep === 0) {
        // Save org name and seed taxonomy
        await apiFetch('tenants/self', {
          method: 'PUT',
          body: JSON.stringify({
            name: step1.orgName,
            settings: { taxonomyPack: step1.taxonomyPack },
            onboarding_checklist: { org_setup: true },
          }),
        });
        if (step1.taxonomyPack) {
          await apiFetch('taxonomies/seed', {
            method: 'POST',
            body: JSON.stringify({ pack: step1.taxonomyPack }),
          });
        }
      } else if (activeStep === 4 && step5.name) {
        // Create first assessment
        await dataProvider.create('assessments', {
          data: { name: step5.name, type: step5.type },
        });
        notify('Assessment created', { type: 'success' });
      } else if (activeStep === 5 && step6.orgPath && step6.seatCount > 0) {
        // Find the first active license, then allocate seats to the chosen org unit
        const { data: licenseList } = await dataProvider.getList('licenses', {
          pagination: { page: 1, perPage: 10 },
          sort: { field: 'activatedAt', order: 'DESC' },
          filter: {},
        });
        const license = licenseList[0] as any;
        if (license) {
          const licId: string = license.licenseId ?? license.id;
          await dataProvider.create(`licenses/${licId}/allocations`, {
            data: { orgPath: step6.orgPath, seatsAllocated: step6.seatCount },
          });
          notify('Seats allocated', { type: 'success' });
        }
      }
    } catch {
      notify('Action failed — continuing anyway', { type: 'warning' });
    } finally {
      setSubmitting(false);
    }

    if (activeStep === STEPS.length - 1) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem('orchestra_onboarding_dismissed', '1');
      notify('Onboarding complete! Welcome to AETC Admin.', { type: 'success' });
      window.location.hash = '#/';
      return;
    }

    const next = activeStep + 1;
    setActiveStep(next);
    persist(next);
  };

  const handleSkip = () => {
    const next = activeStep + 1;
    if (next >= STEPS.length) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem('orchestra_onboarding_dismissed', '1');
      window.location.hash = '#/';
      return;
    }
    setActiveStep(next);
    persist(next);
  };

  const handleBack = () => {
    const prev = Math.max(0, activeStep - 1);
    setActiveStep(prev);
    persist(prev);
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <Step1OrgSetup state={step1} onChange={setStep1} />;
      case 1:
        return <Step2OrgTree />;
      case 2:
        return <Step3InviteAdmins rows={step3Rows} onChange={setStep3Rows} />;
      case 3:
        return <Step4ImportRoster />;
      case 4:
        return <Step5Assessment state={step5} onChange={setStep5} />;
      case 5:
        return <Step6AllocateSeats state={step6} onChange={setStep6} />;
      default:
        return null;
    }
  };

  return (
    <Box p={3} maxWidth={800} mx="auto">
      <Title title="Setup Wizard" />
      <Typography variant="h5" fontWeight={600} mb={3}>
        First-Run Setup
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {STEPS[activeStep]}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {renderStep()}

        <Box display="flex" justifyContent="space-between" mt={4}>
          <Button onClick={handleBack} disabled={activeStep === 0}>
            Back
          </Button>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              onClick={handleSkip}
            >
              {activeStep === STEPS.length - 1 ? 'Skip & Finish' : 'Skip'}
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={submitting}
            >
              {submitting ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : activeStep === STEPS.length - 1 ? (
                'Finish'
              ) : (
                'Next'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
